import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useAccountingStore } from "../../../store/Accounting";
import { TableRowData, PaymentDetail } from "../types/invoice-table.types";
import { Button } from "../../../components/ui/button";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { CustomDateRangePicker } from "../../../components/custom-date-range-picker/custom-date-range-picker.component";
import { AccountingRecipientSelector } from "../components/accounting-recipient-selector.component";
import { useSearchParams } from "react-router-dom";
import { AbstractInvoiceTable } from "../components/abstract-invoice-table.component";
import { TableExportDialog } from "../components/table-export-dialog.component";
import {
	ExportDataResult,
	useTableExport,
} from "../../../hooks/useTableExport";
import {
	EXPORT_MAX_ROWS,
	ExportHeaderMetadata,
} from "../../../lib/utils/export-utils";
import {
	SortingState,
	PaginationState,
	ColumnFiltersState,
} from "@tanstack/react-table";
import { Search, Info, X } from "lucide-react";
import { formatLocalDate } from "@/lib/utils/format-local-date";
import { isConfigTrue } from "../../organization/subpages/administration/konfigurationseinstellungen.page";
import { useCentralStore } from "../../../store/Central";
import { TooltipWrapper } from "../../../components/ui/tooltip-wrapper";

export const StornojournalPage: React.FC = () => {
	const [loading, setLoading] = useState<boolean>(true);
	const { organization } = useCentralStore((state) => ({
		organization: state.organization,
	}));
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedRecipientId, setSelectedRecipientId] = useState<string>(
		searchParams.get("recipient") || ""
	);
	const [highlightedRows, setHighlightedRows] = useState<number[]>([]);
	const [dateValue, setDateValue] = useState<SelectionRange>({
		start: null,
		end: null,
	});

	// Table state for TanStack Table
	const [sorting, setSorting] = useState<SortingState>([
		{ id: "invoice_doc_number", desc: true },
	]);
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 100,
	});
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [globalFilter, setGlobalFilter] = useState<string>("");
	const [totalRowCount, setTotalRowCount] = useState<number>(0);
	const requestIdRef = useRef(0); //necessary to prevent race conditions when fetching data

	const {
		invoicePaymentsData,
		fetchInvoicePaymentsData,
		shareInvoice,
		unshareInvoice,
		recipientsLookup,
	} = useAccountingStore((state) => ({
		invoicePaymentsData: state.canceledInvoicesData,
		fetchInvoicePaymentsData: state.fetchInvoicePaymentsData,
		shareInvoice: state.shareInvoice,
		unshareInvoice: state.unshareInvoice,
		recipientsLookup: state.recipientsLookup,
	}));

	const { exportTable } = useTableExport();

	const formatDate = (date: Date | null): string => {
		if (!date) return "";
		return date.toLocaleDateString("de-CH", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	};

	const headerMetadata: ExportHeaderMetadata = useMemo(() => {
		const recipientName = selectedRecipientId
			? recipientsLookup[selectedRecipientId]?.name
			: undefined;

		const sortingLabel = sorting[0]
			? `${
					sorting[0].id === "invoice_doc_number"
						? "Rechnungsnummer"
						: sorting[0].id === "invoice_date"
							? "Rechnungsdatum"
							: sorting[0].id === "invoice_canceled_at"
								? "Stornodatum"
								: sorting[0].id
				} (${sorting[0].desc ? "absteigend" : "aufsteigend"})`
			: undefined;

		return {
			exportType: "stornojournal" as const,
			exportTypeLabel: "Stornojournal",
			exportDate: formatDate(new Date()),
			dateRange:
				dateValue.start && dateValue.end
					? {
							start: formatDate(dateValue.start),
							end: formatDate(dateValue.end),
						}
					: undefined,
			recipientFilter: recipientName,
			globalFilter: globalFilter || undefined,
			sorting: sortingLabel,
		};
	}, [
		dateValue.start,
		dateValue.end,
		selectedRecipientId,
		recipientsLookup,
		sorting,
		globalFilter,
	]);

	const handleFetchCanceledInvoicesData = useCallback(async () => {
		const currentRequestId = ++requestIdRef.current;
		setLoading(true);

		const sortBy = sorting[0]?.id || "invoice_doc_number";
		const sortDirection = sorting[0]?.desc ? "desc" : "asc";

		try {
			const result = await fetchInvoicePaymentsData(
				dateValue.start ? formatLocalDate(dateValue.start) : "",
				dateValue.end ? formatLocalDate(dateValue.end) : "",
				selectedRecipientId,
				{
					status: "canceled",
					sortBy,
					sortDirection,
					pageIndex: pagination.pageIndex,
					pageSize: pagination.pageSize,
					globalFilter,
					columnFilters,
					updateStore: true,
				}
			);

			if (requestIdRef.current === currentRequestId) {
				if (result) {
					setTotalRowCount(result.totalCount);
				} else {
					setTotalRowCount(0);
				}
			}
		} finally {
			if (requestIdRef.current === currentRequestId) {
				setLoading(false);
			}
		}
	}, [
		fetchInvoicePaymentsData,
		dateValue.start,
		dateValue.end,
		selectedRecipientId,
		sorting,
		pagination,
		globalFilter,
		columnFilters,
	]);

	const handleRecipientChange = (recipientId: string) => {
		setSelectedRecipientId(recipientId);
		if (recipientId) {
			setSearchParams({ recipient: recipientId });
		} else {
			setSearchParams({});
		}
	};

	const fetchExportData = async (): Promise<ExportDataResult> => {
		const currentSortBy = sorting[0]?.id || "invoice_doc_number";
		const currentSortDirection = sorting[0]?.desc ? "desc" : "asc";

		return fetchInvoicePaymentsData(
			dateValue.start ? formatLocalDate(dateValue.start) : "",
			dateValue.end ? formatLocalDate(dateValue.end) : "",
			selectedRecipientId,
			{
				status: "canceled",
				sortBy: currentSortBy,
				sortDirection: currentSortDirection,
				pageIndex: 0,
				pageSize: EXPORT_MAX_ROWS,
				globalFilter,
				columnFilters,
				updateStore: false,
			}
		);
	};

	useEffect(() => {
		const isDateRangeValid =
			(dateValue.start && dateValue.end) ||
			(!dateValue.start && !dateValue.end);
		if (!isDateRangeValid) return;
		handleFetchCanceledInvoicesData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dateValue, selectedRecipientId, sorting, pagination, globalFilter]);

	// Handle sorting changes - reset to page 1 when sorting changes
	const handleSortingChange = (
		updater: SortingState | ((old: SortingState) => SortingState)
	) => {
		setSorting(updater);
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	};

	return (
		<div className="py-6 flex flex-col w-full items-center">
			<div className="w-full max-w-[1920px] px-6">
				{/* Toolbar */}
				<div className="flex items-center justify-between gap-4 py-3 mb-4 border-b border-gray-200">
					{/* Left: Filters */}
					<div className="flex items-center gap-6">
						<TooltipWrapper title="Die Ansicht zeigt alle stornierten Rechnungen inkl. Stornodatum und Zahlungen.">
							<button
								type="button"
								className="text-gray-400 hover:text-gray-600 p-1"
								aria-label="Zweck anzeigen"
							>
								<Info className="h-4 w-4" />
							</button>
						</TooltipWrapper>
						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-500 whitespace-nowrap">
								Zeitraum:
							</span>
							<CustomDateRangePicker
								dateVal={dateValue}
								handleDateChange={setDateValue}
							/>
							{(dateValue.start || dateValue.end) && (
								<button
									type="button"
									onClick={() =>
										setDateValue({ start: null, end: null })
									}
									className="text-gray-400 hover:text-gray-600"
									aria-label="Zeitraum löschen"
								>
									<X size={16} />
								</button>
							)}
						</div>
						<div className="h-8 w-px bg-gray-200" />
						<AccountingRecipientSelector
							selectedRecipientId={selectedRecipientId}
							onRecipientChange={handleRecipientChange}
							recipientList={recipientsLookup}
						/>
						<div className="h-8 w-px bg-gray-200" />
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
							<input
								type="text"
								placeholder="Suchen..."
								value={globalFilter}
								onChange={(e) =>
									setGlobalFilter(e.target.value)
								}
								className="w-64 h-10 pl-10 pr-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
							/>
						</div>
					</div>
					{/* Right: Actions */}
					<div className="flex items-center gap-2">
						{highlightedRows.length > 0 && (
							<Button
								variant="outline"
								onClick={() => setHighlightedRows([])}
							>
								Markierungen zurücksetzen
							</Button>
						)}
						<TableExportDialog
							invoiceData={invoicePaymentsData}
							loading={loading}
							incompleteDateRange={
								Boolean(dateValue.start) !==
								Boolean(dateValue.end)
							}
							onExport={exportTable}
							fetchExportData={fetchExportData}
							currentSorting={
								sorting[0]
									? `${sorting[0].id} (${
											sorting[0].desc
												? "absteigend"
												: "aufsteigend"
										})`
									: "Rechnungsnummer (absteigend)"
							}
							headerMetadata={headerMetadata}
							showPaymentsOption={true}
							showInvoicePdfsOption={true}
						/>
					</div>
				</div>

				<AbstractInvoiceTable<TableRowData>
					data={(() => {
						// Transform invoice data to include payment sub-rows
						if (
							!invoicePaymentsData ||
							!Array.isArray(invoicePaymentsData)
						) {
							return [];
						}
						return invoicePaymentsData
							.filter((invoice) => invoice != null)
							.map((invoice) => ({
								...invoice,
								isPaymentRow: false as const,
								subRows: (
									invoice.payments as unknown as PaymentDetail[]
								)?.map((payment: PaymentDetail) => ({
									...payment,
									invoice_id: invoice.invoice_id || 0,
									isPaymentRow: true as const,
								})),
							}));
					})()}
					tableType="canceled"
					loading={loading}
					highlightedRows={highlightedRows}
					features={{
						allowSharing: true,
						allowZak: isConfigTrue(
							organization?.config,
							"zak_enabled"
						),
						allowPayments: true, // Allow payment actions for canceled invoices
						allowBookAsLoss: false, // Not relevant for canceled invoices
						allowCancelInvoice: false, // Can't cancel already canceled invoices
						readOnlyMode: true, // Canceled invoices are in read-only mode
						historicalView: false,
					}}
					uiCallbacks={{
						onRefreshData: handleFetchCanceledInvoicesData,
					}}
					// Table state
					sorting={sorting}
					onSortingChange={handleSortingChange}
					pagination={pagination}
					onPaginationChange={setPagination}
					globalFilter={globalFilter}
					onGlobalFilterChange={setGlobalFilter}
					// Data fetching
					totalRowCount={totalRowCount}
					manualSorting={true}
					manualPagination={true}
					manualFiltering={true}
					showVATSummary={false}
					emptyStateLabel="Keine stornierten Rechnungen vorhanden"
				/>
			</div>
		</div>
	);
};
